import { problemText } from '@/shared/api/problemText';
import { useApiProblem } from '@/shared/api/useApiProblem';
import { Button } from '@/shared/components';

export interface MessageListStatus {
  isLoading: boolean;
  paused?: boolean;
  isError: boolean;
  error: unknown;
  incomplete: boolean;
  refetch: () => void;
}

/**
 * Zustände des Verlaufs: lädt, keine Verbindung, nicht geladen (mit Grund), unvollständig, leer.
 * Steht schon Verlauf da, gehört der Satz ans Ende — dorthin hält der Verlauf, und dort fehlt, was
 * nicht geladen wurde; über der ältesten Nachricht sähe ihn niemand.
 */
export function MessageListState({
  status,
  empty,
}: {
  status?: MessageListStatus;
  empty: boolean;
}) {
  const problem = useApiProblem(status?.isError ? status.error : null);
  const quiet = 'text-ink-faint self-center text-sm';
  if (status?.isLoading) {
    return (
      <p role="status" className={quiet}>
        Verlauf wird geladen …
      </p>
    );
  }
  if (status?.paused) {
    return (
      <p role="status" className={quiet}>
        Keine Verbindung. Der Verlauf lädt, sobald sie zurück ist.
      </p>
    );
  }
  if (status?.isError) {
    return (
      <div
        role="alert"
        className="border-hairline rounded-tile flex flex-wrap items-center gap-3 self-stretch border bg-white p-3"
      >
        <p className="min-w-0 flex-1 text-sm">
          {problemText(
            problem,
            empty
              ? 'Der Verlauf konnte nicht geladen werden.'
              : 'Nachladen fehlgeschlagen – neuere Nachrichten können fehlen; zu sehen ist der zuletzt geladene Stand.',
          )}
        </p>
        <Button type="button" variant="secondary" className="min-h-11" onClick={status.refetch}>
          Erneut laden
        </Button>
      </div>
    );
  }
  if (status?.incomplete) {
    return (
      <p role="status" className="text-ink-faint self-center text-xs">
        Neuere Nachrichten werden geladen …
      </p>
    );
  }
  if (empty) {
    return <p className={quiet}>Noch keine Nachrichten in dieser Unterhaltung.</p>;
  }
  return null;
}
